import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';

import { getInviteAdminSummary } from '../../api/inviteApi';
import { auth } from '../../firebase/config';
import './InviteOperationsPage.css';


const number = (value) => Number(value || 0).toLocaleString();
const SURFACE_LABELS = {
  account: '내 계정',
  chat: 'AI 채팅',
  'hwp-chat': 'AI 한글',
  library: '라이브러리',
  'assessment-result': '평가계획서',
};

const InviteOperationsPage = () => {
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      setSummary(await getInviteAdminSummary(user));
    } catch (loadError) {
      setError(
        loadError?.message
        || '초대 운영 지표를 불러오지 못했어요.'
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) load();
    if (!authLoading && !user) setLoading(false);
  }, [authLoading, load, user]);

  const funnel = summary?.funnel || {};
  const rewards = summary?.rewards || {};
  const policy = summary?.policy || {};
  const surfaces = Object.entries(summary?.share_surfaces || {});
  const programEnabled = Boolean(policy.program_enabled);

  return (
    <main className="invite-ops-page">
      <Helmet>
        <title>친구 초대 운영 | 관리자</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="invite-ops-header">
        <button
          className="invite-ops-back"
          onClick={() => navigate('/admin')}
          type="button"
        >
          관리자 홈
        </button>
        <div>
          <p>성장 운영</p>
          <h1>친구 초대</h1>
          <span>개인정보 없이 공유부터 결제까지 확인해요.</span>
        </div>
        <button
          className="invite-ops-refresh"
          disabled={loading || !user}
          onClick={load}
          type="button"
        >
          새로고침
        </button>
      </header>

      {!authLoading && !user && (
        <section className="invite-ops-state" role="alert">
          관리자 로그인이 필요해요.
        </section>
      )}

      {error && (
        <section className="invite-ops-state invite-ops-state-error" role="alert">
          <p>{error}</p>
          <button onClick={load} type="button">다시 불러오기</button>
        </section>
      )}

      {loading ? (
        <section className="invite-ops-grid" aria-label="초대 지표 불러오는 중">
          {[0, 1, 2, 3].map((item) => (
            <div className="invite-ops-skeleton" key={item} />
          ))}
        </section>
      ) : summary && (
        <>
          <section className="invite-ops-policy">
            <div>
              <span className={programEnabled ? 'is-on' : 'is-off'}>
                {programEnabled ? '운영 중' : '운영 전'}
              </span>
              <strong>
                가입 {policy.signup_days_each || 0}일 · 첫 결제
                {' '}{number(policy.first_payment_discount)}원 · 전환
                {' '}{policy.conversion_days || 0}일
              </strong>
            </div>
            <p>
              추천인 자동 지급: 가입 월 {number(policy.monthly_signup_cap)}건 ·
              {' '}결제 월 {number(policy.monthly_conversion_cap)}건
              <br />
              가입·결제·환불 알림은 쿠폰·무료체험 Slack 채널로 전송돼요.
            </p>
          </section>

          <section className="invite-ops-grid" aria-label="핵심 초대 지표">
            <article>
              <span>공유 화면 열림</span>
              <strong>{number(funnel.share_opened)}</strong>
              <p>추천 코드 포함 {number(funnel.referral_attached)}</p>
            </article>
            <article>
              <span>가입 연결</span>
              <strong>{number(funnel.attributed)}</strong>
              <p>최근 30일 {number(funnel.attributed_last_30d)}</p>
            </article>
            <article>
              <span>첫 결제</span>
              <strong>{number(funnel.paid_conversions)}</strong>
              <p>환불 {number(funnel.refunded_conversions)}</p>
            </article>
            <article>
              <span>누적 순지급 PRO</span>
              <strong>{number(rewards.net_pro_days)}일</strong>
              <p>
                가입 {number(rewards.signup_pro_days)}일 ·
                {' '}결제 {number(rewards.conversion_pro_days)}일
              </p>
            </article>
          </section>

          <section className="invite-ops-detail-grid">
            <article className="invite-ops-panel">
              <h2>전환 흐름</h2>
              <dl>
                <div>
                  <dt>공유 → 가입 연결</dt>
                  <dd>{funnel.share_to_attribution_rate || 0}%</dd>
                </div>
                <div>
                  <dt>가입 연결 → 첫 결제</dt>
                  <dd>{funnel.attribution_to_payment_rate || 0}%</dd>
                </div>
                <div>
                  <dt>첫 결제 할인 사용</dt>
                  <dd>{number(funnel.discounts_used)}건</dd>
                </div>
                <div>
                  <dt>이번 달 가입 보상</dt>
                  <dd>{number(rewards.monthly_signup_count)}건</dd>
                </div>
                <div>
                  <dt>이번 달 결제 보상</dt>
                  <dd>{number(rewards.monthly_conversion_count)}건</dd>
                </div>
                <div>
                  <dt>상한 도달</dt>
                  <dd>
                    가입 {number(rewards.signup_capped)}건 ·
                    {' '}결제 {number(rewards.conversion_capped)}건
                  </dd>
                </div>
              </dl>
            </article>

            <article className="invite-ops-panel">
              <h2>공유 위치</h2>
              {surfaces.length > 0 ? (
                <ul>
                  {surfaces.map(([surface, count]) => (
                    <li key={surface}>
                      <span>{SURFACE_LABELS[surface] || surface}</span>
                      <strong>{number(count)}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="invite-ops-empty">아직 공유 기록이 없어요.</p>
              )}
            </article>
          </section>

          {summary.limits?.truncated_possible && (
            <p className="invite-ops-limit-note">
              집계 상한에 도달했어요. 장기 추이는 별도 일별 집계로 전환해야 해요.
            </p>
          )}
        </>
      )}
    </main>
  );
};

export default InviteOperationsPage;
